import { service } from "@ember/service";
import { action } from "@ember/object";
import Controller from "@ember/controller";
import { task, timeout } from "ember-concurrency";
import { tracked } from "@glimmer/tracking";
import { queryRecord, saveRecord } from "@warp-drive/legacy/compat/builders";
import ENV from "glesys-controlpanel/config/environment";

export default class FileStorageVolumesDetailsController extends Controller {
  @service ajax;
  @service modal;
  @service router;
  @service store;
  @service storage;

  get volume() {
    return this.model.fileStorageVolume;
  }

  get plans() {
    return this.model.plans.plans;
  }

  get currentPlan() {
    return this.volume.plan;
  }

  get currentName() {
    return this.volume.name;
  }

  get project() {
    return this.model.project;
  }

  get servers() {
    return this.model.servers;
  }

  get accessList() {
    return this.volume.accessList;
  }

  get accessListOfHostnames() {
    let servers = this.servers;
    let accessList = this.accessList;

    return servers.filter((server) => accessList.includes(server.id)).map((server) => server.hostname);
  }

  get usageStatistics() {
    return this.statistics?.usage.map((item) => {
      return {
        key: item.timestamp,
        value: item.value,
      };
    });
  }

  get loadStatistics() {
    return this.statistics?.load;
  }

  @tracked statistics;
  @tracked selectedUsageGuideOS = "linux";

  get deleteButtonStyle() {
    if (this.volume.isLocked) {
      return "bg-gray-400 cursor-not-allowed";
    } else {
      return "bg-red-600 hover:bg-red-800";
    }
  }

  deleteFileStorageVolume = task(async () => {
    let volume = this.volume;

    volume.deleteRecord();

    await this.store.request(saveRecord(volume)).catch((error) => {
      volume.rollbackAttributes();
      this.router.transitionTo("file-storage-volumes.details", volume.id);
      throw error;
    });

    this.router.transitionTo("file-storage-volumes.overview");
  });

  serverAccessListTask = task(async (accessList) => {
    let volume = this.volume;
    volume.accessList = accessList ?? [];

    await this.store.request(saveRecord(volume)).catch((error) => {
      volume.rollbackAttributes();
      throw error;
    });
  });

  createServerTask = task(async () => {
    await this.router.transitionTo("servers.create");
  });

  updateSettingsTask = task(async (newPlan, newAutoPromote) => {
    let { volume } = this;
    volume.hasAutoPromotion = newAutoPromote;
    volume.plan = newPlan ?? this.volume.plan;

    await this.store.request(saveRecord(volume)).catch((error) => {
      volume.rollbackAttributes();
      throw error;
    });
  });

  updateDetailsTask = task(async (newDetails) => {
    let { volume } = this;
    let name = volume.name;
    volume.name = newDetails ?? name;

    await this.store.request(saveRecord(volume)).catch((error) => {
      volume.rollbackAttributes();
      throw error;
    });
  });

  fetchVolumeLoopTask = task(async () => {
    // istanbul ignore else
    if (ENV.environment === "test") {
      return;
    }

    // istanbul ignore next
    while (true) {
      if (this.volume.get("currentState.stateName") === "root.loaded.saved") {
        await this.store.request(
          queryRecord("file-storage-volume", {
            volumeid: this.volume.get("id"),
            project: this.project.get("id"),
          }),
        );
      }

      let interval = this.volume.get("status") === "ready" ? 10000 : 2000;
      await timeout(interval);
    }
  });

  fetchVolumeStatisticsLoopTask = task(async () => {
    // istanbul ignore else
    if (ENV.environment === "test") {
      return;
    }

    // istanbul ignore next
    while (true) {
      if (this.volume.get("currentState.stateName") === "root.loaded.saved") {
        let response = await this.ajax.request(
          `${ENV.apiBaseUri}/filestorage/resourceusage?volumeid=${this.volume.id}`,
        );

        this.statistics = response.resourceusage;
      }

      await timeout(10 * 1000);
    }
  });

  setupController() {
    this.fetchVolumeStatisticsLoopTask.perform();
    this.fetchVolumeLoopTask.perform();
  }

  resetController() {
    this.fetchVolumeStatisticsLoopTask.cancelAll();
    this.fetchVolumeLoopTask.cancelAll();
  }

  @action
  openFileStorageVolumeUpdateDetailsModal() {
    let properties = {
      currentName: this.currentName,
      updateDetailsTask: this.updateDetailsTask,
    };
    this.modal.open("modals/file-storage-volumes-update-details", properties);
  }

  @action
  openFileStorageVolumeUpdateSettingsModal() {
    let properties = {
      currentPlan: this.currentPlan,
      plans: this.plans,
      updateSettingsTask: this.updateSettingsTask,
      volume: this.volume,
    };
    this.modal.open("modals/file-storage-volumes-update-settings", properties);
  }

  @action
  openFileStorageVolumeUpdateAccessModal() {
    let properties = {
      project: this.project,
      servers: this.servers,
      accessList: this.accessList,
      serverAccessListTask: this.serverAccessListTask,
      createServerTask: this.createServerTask,
      volumeId: this.volume.id,
    };

    this.modal.open("modals/file-storage-volumes-update-access", properties);
  }

  @action
  setUsageGuideOS(os) {
    this.selectedUsageGuideOS = os;
  }
}
